import React, { useState } from 'react';
import { Pressable, PressableProps } from 'react-native';
import { DNABox, DNABoxProps, DNAIcon, DNASelect, DNAText } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { AISearchFlow, getAvailableAISearchFlows, getAISearchFlow } from 'src/types/aiSearchFlows';

const PressableDNABox = DNABox as React.ComponentType<DNABoxProps & PressableProps>;

const FlowInfo: React.FC<{ flow: AISearchFlow }> = ({ flow }) => {
  const [visible, setVisible] = useState<boolean | undefined>();
  return (
    <DNAPopover
      placement="bottom"
      visible={visible}
      onBackdropPress={() => setVisible(undefined)}
    >
      <DNAPopover.Anchor>
        <PressableDNABox
          as={Pressable}
          accessibilityRole="button"
          accessibilityLabel={`About ${flow.label}`}
          accessibilityHint={flow.description}
          onPress={(event) => {
            event.stopPropagation();
            setVisible(!visible);
          }}
          onHoverOut={() => setVisible(undefined)}
          onBlur={() => setVisible(undefined)}
          style={{ padding: 6 }}
        >
          <DNAIcon.Styled name="info" status="subtle" size="sm" />
        </PressableDNABox>
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNABox style={{ maxWidth: 280, padding: 4 }}>
          <DNAText colorMode="dark">{flow.description}</DNAText>
        </DNABox>
      </DNAPopover.Content>
    </DNAPopover>
  );
};

const AISearchFlowSelector: React.FC = () => {
  const { isAiSearch, aiSearchFlowId, setAISearchFlowId, aiSearchSessionData } = useDocumentSearchV2Context();
  const isSelectorEnabled = useFeatureFlags('enableAISearchFlowSelector');
  const isDocumentSearchAgentEnabled = useFeatureFlags('BEAC_7776_document_search_agent_flow');
  const isLunaEnabled = useFeatureFlags('BEAC_8540_ai_search_luna');
  const flows = getAvailableAISearchFlows(isLunaEnabled);
  const flow = getAISearchFlow(aiSearchFlowId, isSelectorEnabled, isDocumentSearchAgentEnabled, isLunaEnabled);

  if (!isAiSearch || !isSelectorEnabled) return null;

  return (
    <DNABox
      appearance="row"
      alignY="center"
      style={{ width: 250, maxWidth: '45%', marginLeft: 8 }}
      testID="ai-search-flow-selector"
    >
      <DNASelect
        accessibilityLabel="AI Search flow"
        value={flow.label}
        selectedIndex={new DNASelect.Index(flows.indexOf(flow))}
        disabled={aiSearchSessionData.loading}
        style={{ flex: 1, minWidth: 0 }}
        onSelect={(index) => {
          if (!Array.isArray(index) && !aiSearchSessionData.loading) {
            const selectedFlow = flows[index.row];
            if (selectedFlow && selectedFlow.id !== flow.id) setAISearchFlowId(selectedFlow.id);
          }
        }}
      >
        {flows.map((option) => (
          <DNASelect.Item
            key={option.id}
            title={option.label}
            accessoryRight={() => <FlowInfo flow={option} />}
          />
        ))}
      </DNASelect>
      <FlowInfo key={flow.id} flow={flow} />
    </DNABox>
  );
};

export default AISearchFlowSelector;
